/**
 * Audience Q&A (#118) — the audience asks, a moderator approves, the
 * presenter answers.
 *
 * **`AskedQuestion`, not `Question`.** `Question` in types.ts is a poll
 * question an author wrote; this is a question somebody in the room typed on
 * their phone. The two are unrelated and both are called "question" in the
 * UI, so the code names carry the disambiguation — the same trade the
 * Quiz/poll vocabulary rule already makes.
 *
 * The audience is anonymous: a taker has no account and never will, so the
 * only identity on an asked question is the device token the join flow already
 * mints. That is what decides most of the shape below.
 */

export const ASKED_QUESTION_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type AskedQuestionStatus = (typeof ASKED_QUESTION_STATUSES)[number];

export function isAskedQuestionStatus(value: unknown): value is AskedQuestionStatus {
  return (
    typeof value === 'string' && (ASKED_QUESTION_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * A question from the floor, as the console sees it — every field, including
 * the three no public surface may have.
 *
 * There is deliberately no `quizId` here. It is in the item's partition key
 * and every caller already knows which poll it asked about, so leaving it off
 * means the public projection below has one less thing to remember to withhold
 * — and a taker is never told a quizId (see `PublicQuizState`).
 */
export interface AskedQuestion {
  askedQuestionId: string;
  /** Plain text, never rendered as HTML or markdown. */
  content: string;
  askedAt: string;
  status: AskedQuestionStatus;
  /** Audience upvotes. Always present, so a client never has to default it. */
  likes: number;
  /**
   * The device token that submitted this. **Withheld from every public
   * projection** — see `publicAskedQuestion` in services/presentation.ts.
   *
   * Stored so this device can be shown its own questions again after a
   * refresh, exactly as `followUpsForAnswers` replays a follow-up. It is not
   * a person and must never be published: an anonymous room stops being
   * anonymous the moment two questions can be tied to one phone.
   */
  askedBy?: string;
  approvedAt?: string;
  /** The moderator's user id. Withheld from every public projection. */
  approvedBy?: string;
  /**
   * Why a moderator turned this down. Withheld from every *broadcast*, and
   * returned only to the device that asked — the `followUp` pattern verbatim.
   *
   * The rejection itself is published as a bare id, which is how that device
   * knows to come and ask for this. The room learns that some question was
   * turned down; it never learns which, by whom, or why.
   */
  rejectionReason?: string;
  /** Presentation order, set by the moderator on approval. */
  order?: number;
  /** What the presenter said, when they chose to record it. */
  answer?: string;
  answeredAt?: string;
}

/**
 * What a public surface may see: a phone, a projector, or a presenter's own
 * panel.
 *
 * Built field by field by `publicAskedQuestion`, whose return type names these
 * three in an `Omit` so putting one back is a build error rather than a review
 * catch — the same construction as `publicOption`.
 */
export type PublicAskedQuestion = Omit<AskedQuestion, 'askedBy' | 'approvedBy' | 'rejectionReason'>;

/**
 * One of this device's own questions, replayed to the phone that submitted it.
 *
 * Carries `rejectionReason`, which no broadcast does: the asker is the one
 * person entitled to know their question was turned down. `askedBy` and
 * `approvedBy` stay withheld — the first it already knows, the second names a
 * moderator to a stranger.
 */
export type OwnAskedQuestion = Omit<AskedQuestion, 'askedBy' | 'approvedBy'>;

/**
 * The moderator's queue, split by status.
 *
 * Three arrays rather than one list plus a status field, because the queue is
 * three columns on screen and the split is the read the moderator actually
 * makes. The counts ride along so a badge does not have to be derived from a
 * paginated list.
 */
export interface AskedQuestionQueue {
  pending: AskedQuestion[];
  approved: AskedQuestion[];
  rejected: AskedQuestion[];
}

/**
 * How long a device must wait between questions.
 *
 * The join code is the only gate on a route anyone on the internet can write
 * to, so this is the first of two ceilings — per device here, per run below.
 */
export const ASK_COOLDOWN_MS = 30_000;

/**
 * How many questions one presentation run holds **at once**, across every
 * device.
 *
 * A per-device cooldown alone bounds nothing: tokens are minted by asking for
 * one. This bounds the partition, and it is what keeps "read every question
 * for this poll" a single query rather than a paginated sweep.
 *
 * At once, not over the run's lifetime, and that distinction is the whole
 * reason `deleteAskedQuestion` refunds a slot. A lifetime budget would let a
 * burst of junk switch Q&A off for the evening with no remedy a moderator
 * could apply — deleting it all would give nothing back.
 */
export const MAX_ASKED_QUESTIONS_PER_RUN = 200;

/**
 * Sorts a question list for reading: by the moderator's order where one is
 * set, oldest first otherwise.
 *
 * Shared so the console, the phone and the projector agree. An unordered
 * question sorts after every ordered one rather than at position zero —
 * approving without naming an order should append, not jump the queue.
 */
export function byPresentationOrder(a: AskedQuestion, b: AskedQuestion): number {
  const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return a.askedAt < b.askedAt ? -1 : a.askedAt > b.askedAt ? 1 : 0;
}
